import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface Volunteer {
  _id: string;
  fullName: string;
  email: string;
  phone: string;
  city: string;
  province: string;
  interests: string[];
  skills: string;
  experience: string;
  availability: string;
  motivation: string;
  agreeTerms: boolean;
  subscribeNewsletter: boolean;
  status?: 'pending' | 'approved' | 'rejected' | 'contacted';
  createdAt: string;
  notes?: string;
}

const Volunteers: React.FC = () => {
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedVolunteer, setSelectedVolunteer] = useState<Volunteer | null>(null);
  const [notes, setNotes] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    province: '',
    search: ''
  });

  const getAuthToken = () => localStorage.getItem('token');

  useEffect(() => {
    fetchVolunteers();
  }, [filters]);

  const fetchVolunteers = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      if (!token) {
        setError('Authentication required');
        return;
      }

      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.province) params.append('province', filters.province);
      if (filters.search) params.append('search', filters.search);

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/volunteers?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setVolunteers(data.data || []);
      } else {
        setError('Failed to fetch volunteers');
      }
    } catch (err) {
      setError('Network error occurred');
      console.error('Volunteers fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateVolunteerStatus = async (volunteerId: string, newStatus: string, notesText?: string) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const updateData: any = { status: newStatus };
      if (notesText) updateData.notes = notesText;

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/volunteers/${volunteerId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        await fetchVolunteers(); // Refresh the list
        setSelectedVolunteer(null);
        setNotes('');
      }
    } catch (err) {
      console.error('Update error:', err);
    }
  };

  const deleteVolunteer = async (volunteerId: string) => {
    if (!window.confirm('Are you sure you want to delete this volunteer application?')) return;

    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000'}/api/volunteers/${volunteerId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchVolunteers();
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const getStatusBadgeClass = (status?: string) => {
    switch (status) {
      case 'pending': return 'bg-orange-100 text-orange-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'contacted': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const provinces = [
    'Punjab', 'Sindh', 'Khyber Pakhtunkhwa', 'Balochistan', 
    'Gilgit-Baltistan', 'Azad Kashmir', 'Islamabad Capital Territory'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading volunteers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <Link to="/admin/dashboard" className="text-green-600 hover:text-green-700 mb-2 inline-block">
                ← Back to Dashboard
              </Link>
              <h1 className="text-2xl font-bold text-gray-900">Volunteer Management</h1>
              <p className="text-gray-600">Manage volunteer applications and recruitment</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                placeholder="Search by name, email, city..."
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="contacted">Contacted</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Province</label>
              <select
                value={filters.province}
                onChange={(e) => setFilters({...filters, province: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="">All Provinces</option>
                {provinces.map(province => (
                  <option key={province} value={province}>{province}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({status: '', province: '', search: ''})}
                className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Volunteers List */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Volunteer Applications ({volunteers.length})</h2>
          </div>

          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="divide-y divide-gray-200">
            {volunteers.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-gray-400 text-6xl mb-4">🤝</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No volunteers found</h3>
                <p className="text-gray-600">No volunteer applications match your current filters.</p>
              </div>
            ) : (
              volunteers.map((volunteer) => (
                <div key={volunteer._id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <div>
                        <h3 className="text-lg font-semibold">{volunteer.fullName}</h3>
                        <p className="text-sm text-gray-600">{volunteer.email} • {volunteer.phone}</p>
                        <p className="text-sm text-gray-500">{volunteer.city}, {volunteer.province}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(volunteer.status)}`}>
                        {volunteer.status || 'pending'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">
                        {new Date(volunteer.createdAt).toLocaleDateString()}
                      </span>
                      <button
                        onClick={() => setSelectedVolunteer(volunteer)}
                        className="text-green-600 hover:text-green-700 font-medium"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => deleteVolunteer(volunteer._id)}
                        className="text-red-600 hover:text-red-700 font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-900 mb-1">Availability: {volunteer.availability}</h4>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {volunteer.interests.map((interest, index) => (
                        <span key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                          {interest}
                        </span>
                      ))}
                    </div>
                    <p className="text-gray-700 text-sm line-clamp-2">{volunteer.motivation}</p>
                  </div>

                  <div className="flex space-x-2">
                    {(!volunteer.status || volunteer.status === 'pending') && (
                      <>
                        <button
                          onClick={() => updateVolunteerStatus(volunteer._id, 'approved')}
                          className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => updateVolunteerStatus(volunteer._id, 'rejected')}
                          className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => updateVolunteerStatus(volunteer._id, 'contacted')}
                      className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                    >
                      Mark Contacted
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Volunteer Detail Modal */}
      {selectedVolunteer && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Volunteer Application Details</h2>
                <button
                  onClick={() => setSelectedVolunteer(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-900 mb-4">Personal Information</h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Full Name:</span>
                      <p className="text-gray-900">{selectedVolunteer.fullName}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Email:</span>
                      <p className="text-gray-900">{selectedVolunteer.email}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Phone:</span>
                      <p className="text-gray-900">{selectedVolunteer.phone}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Location:</span>
                      <p className="text-gray-900">{selectedVolunteer.city}, {selectedVolunteer.province}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Status:</span>
                      <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(selectedVolunteer.status)}`}>
                        {selectedVolunteer.status || 'pending'}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-medium text-gray-900 mb-4">Application Details</h3>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm font-medium text-gray-500">Availability:</span>
                      <p className="text-gray-900">{selectedVolunteer.availability}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Applied:</span>
                      <p className="text-gray-900">{new Date(selectedVolunteer.createdAt).toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Newsletter:</span>
                      <p className="text-gray-900">{selectedVolunteer.subscribeNewsletter ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Interests */}
              <div>
                <h3 className="font-medium text-gray-900 mb-3">Areas of Interest</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedVolunteer.interests.map((interest, index) => (
                    <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                      {interest}
                    </span>
                  ))}
                </div>
              </div>

              {/* Skills */}
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Skills & Expertise</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedVolunteer.skills || 'Not specified'}</p>
              </div>

              {/* Experience */}
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Previous Experience</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedVolunteer.experience || 'Not specified'}</p>
              </div>

              {/* Motivation */}
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Motivation</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedVolunteer.motivation}</p>
              </div>

              {/* Admin Actions */}
              <div className="border-t pt-6">
                <h3 className="font-medium text-gray-900 mb-4">Admin Actions</h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Add Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Add internal notes about this volunteer..."
                  />
                </div>

                <div className="flex space-x-3">
                  {(!selectedVolunteer.status || selectedVolunteer.status === 'pending') && (
                    <>
                      <button
                        onClick={() => updateVolunteerStatus(selectedVolunteer._id, 'approved', notes)}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                      >
                        Approve Application
                      </button>
                      <button
                        onClick={() => updateVolunteerStatus(selectedVolunteer._id, 'rejected', notes)}
                        className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                      >
                        Reject Application
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => updateVolunteerStatus(selectedVolunteer._id, 'contacted', notes)}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                  >
                    Mark as Contacted
                  </button>
                  {notes && (
                    <button
                      onClick={() => updateVolunteerStatus(selectedVolunteer._id, selectedVolunteer.status || 'pending', notes)}
                      className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                    >
                      Save Notes Only
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Volunteers;
